/**
 * Proceduralne FX blasku kotka (§8.2) — bez PNG glow.
 * Pojawienie: mocny blask 600 ms. Pomoc: słabszy glow + float y-24.
 */
import Phaser from 'phaser';

export type CompanionFxHandles = {
  /** Zatrzymaj wszystkie aktywne FX. */
  stop: () => void;
  /** Offset nakładany przez CompanionLayer, bez walki tweena z follow. */
  offsetY?: () => number;
};

const APPEAR_MS = 600;
const HELP_FLOAT_Y = -24;

export const CompanionFx = {
  /**
   * Mocny blask pojawienia: flash biało-złoty + pierścień „gwiazdek”.
   */
  playAppear(
    scene: Phaser.Scene,
    target: Phaser.GameObjects.Container | Phaser.GameObjects.Image,
  ): CompanionFxHandles {
    const x = 'x' in target ? target.x : 0;
    const y = 'y' in target ? target.y : 0;

    const flash = scene.add.circle(x, y, 48, 0xfff8dc, 0.85);
    flash.setDepth(1000);
    flash.setBlendMode(Phaser.BlendModes.ADD);

    const ring = scene.add.circle(x, y, 20, 0xffd700, 0.0);
    ring.setStrokeStyle(4, 0xfffaf0, 0.9);
    ring.setDepth(999);
    ring.setBlendMode(Phaser.BlendModes.ADD);

    const particles: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < 8; i++) {
      const ang = (i / 8) * Math.PI * 2;
      const p = scene.add.circle(
        x + Math.cos(ang) * 12,
        y + Math.sin(ang) * 12,
        4,
        0xffe566,
        1,
      );
      p.setDepth(1001);
      p.setBlendMode(Phaser.BlendModes.ADD);
      particles.push(p);
      scene.tweens.add({
        targets: p,
        x: x + Math.cos(ang) * 56,
        y: y + Math.sin(ang) * 56,
        alpha: 0,
        scale: 0.2,
        duration: APPEAR_MS,
        ease: 'Cubic.Out',
        onComplete: () => p.destroy(),
      });
    }

    scene.tweens.add({
      targets: flash,
      scale: 2.2,
      alpha: 0,
      duration: APPEAR_MS,
      ease: 'Cubic.Out',
      onComplete: () => flash.destroy(),
    });
    scene.tweens.add({
      targets: ring,
      scale: 3,
      alpha: 0,
      duration: APPEAR_MS,
      ease: 'Cubic.Out',
      onComplete: () => ring.destroy(),
    });

    // lekki „postFX glow” przez skalę celu
    const baseScaleX = target.scaleX;
    const baseScaleY = target.scaleY;
    scene.tweens.add({
      targets: target,
      scaleX: baseScaleX * 1.15,
      scaleY: baseScaleY * 1.15,
      duration: APPEAR_MS / 2,
      yoyo: true,
      ease: 'Sine.InOut',
    });

    return {
      stop: () => {
        flash.destroy();
        ring.destroy();
        for (const p of particles) p.destroy();
      },
    };
  },

  /**
   * Słabszy blask + unoszenie na czas hintu (glow ~0.4, y -24, yoyo).
   */
  playHelp(
    scene: Phaser.Scene,
    target: Phaser.GameObjects.Container | Phaser.GameObjects.Image,
    durationMs = 2000,
  ): CompanionFxHandles {
    const glow = scene.add.circle(target.x, target.y, 40, 0xfff4c1, 0.4);
    glow.setDepth((target.depth ?? 0) - 1);
    glow.setBlendMode(Phaser.BlendModes.ADD);

    const follow = () => {
      glow.setPosition(target.x, target.y);
    };
    scene.events.on('update', follow);

    const floatState = { offsetY: 0 };
    const floatTween = scene.tweens.add({
      targets: floatState,
      offsetY: HELP_FLOAT_Y,
      duration: 500,
      yoyo: true,
      repeat: Math.max(0, Math.floor(durationMs / 1000) - 1),
      ease: 'Sine.InOut',
    });

    const pulseTween = scene.tweens.add({
      targets: glow,
      alpha: 0.15,
      scale: 1.3,
      duration: 400,
      yoyo: true,
      repeat: -1,
    });

    const timer = scene.time.delayedCall(durationMs, () => {
      scene.events.off('update', follow);
      floatTween.stop();
      pulseTween.stop();
      floatState.offsetY = 0;
      glow.destroy();
    });

    return {
      offsetY: () => floatState.offsetY,
      stop: () => {
        scene.events.off('update', follow);
        timer.remove(false);
        floatTween.stop();
        pulseTween.stop();
        floatState.offsetY = 0;
        glow.destroy();
      },
    };
  },
};
